import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import PaginationBar from '../components/PaginationBar';
import { Search, MapPin, Briefcase, Users, Building2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PAGE_SIZE = 12;

export default function RecruitersDirectoryPage() {
  const [recruiters, setRecruiters] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  // A new search always starts from the first page.
  useEffect(() => { setPage(1); }, [search]);

  useEffect(() => {
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API_URL}/api/recruiters`, {
          params: { ...(search ? { search } : {}), skip: (page - 1) * PAGE_SIZE, limit: PAGE_SIZE },
        });
        setRecruiters(res.data?.recruiters || []);
        setTotal(res.data?.total || 0);
      } catch {
        setRecruiters([]);
        setTotal(0);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [search, page]);

  return (
    <div className="min-h-screen bg-[#F0F4F8]">
      <div className="bg-gradient-to-br from-[#003366] to-[#004080] text-white py-16 px-4">
        <div className="max-w-5xl mx-auto text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Users className="w-10 h-10 text-white/80" />
            <h1 className="text-4xl font-bold">Recruiters Directory</h1>
          </div>
          <p className="text-white/80 text-lg mb-8">Browse recruiters and see every job they have posted</p>
          <div className="bg-white rounded-2xl p-3 flex items-center gap-2 max-w-2xl mx-auto shadow-lg">
            <Search className="w-5 h-5 text-muted-foreground shrink-0 ml-2" />
            <input
              type="text"
              placeholder="Search by name, company or location..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="flex-1 text-gray-800 outline-none text-sm bg-transparent py-1"
            />
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-10">
        <p className="text-sm text-muted-foreground mb-6">
          {loading ? 'Loading...' : `${total.toLocaleString()} recruiter${total !== 1 ? 's' : ''}`}
        </p>

        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3, 4, 5, 6].map((i) => <Skeleton key={i} className="h-40 rounded-xl" />)}
          </div>
        ) : recruiters.length === 0 ? (
          <Card><CardContent className="py-16 text-center text-muted-foreground">No recruiters found.</CardContent></Card>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {recruiters.map((r) => (
              <Link key={r.id} to={`/recruiters/${r.id}`} data-testid={`recruiter-card-${r.id}`}>
                <Card className="h-full hover:shadow-md transition-shadow">
                  <CardContent className="p-5">
                    <div className="flex items-start gap-4">
                      <div className="w-14 h-14 rounded-full bg-[#003366]/10 flex items-center justify-center shrink-0 overflow-hidden">
                        {r.avatar_url
                          ? <img src={r.avatar_url} alt={r.full_name} className="w-full h-full object-cover" />
                          : <span className="text-lg font-semibold text-[#003366]">{(r.full_name || '?').charAt(0).toUpperCase()}</span>}
                      </div>
                      <div className="min-w-0">
                        <h3 className="font-semibold truncate">{r.full_name}</h3>
                        {r.company_name && (
                          <p className="text-sm text-muted-foreground flex items-center gap-1 truncate">
                            <Building2 className="w-3.5 h-3.5 shrink-0" />{r.company_name}
                          </p>
                        )}
                        {r.location && (
                          <p className="text-sm text-muted-foreground flex items-center gap-1 truncate">
                            <MapPin className="w-3.5 h-3.5 shrink-0" />{r.location}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="mt-4">
                      <Badge variant="secondary" className="gap-1">
                        <Briefcase className="w-3 h-3" />{r.active_jobs_count} active job{r.active_jobs_count !== 1 ? 's' : ''}
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}

        {!loading && recruiters.length > 0 && (
          <PaginationBar page={page} pageSize={PAGE_SIZE} total={total} onPageChange={setPage} />
        )}
      </div>
    </div>
  );
}
